(() => {
  const formatMoney = (cents) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format((Number(cents) || 0) / 100);

  class SmashboxxConfigurator {
    constructor(root) {
      this.root = root;
      this.data = JSON.parse(root.querySelector('[data-configurator-data]').textContent);
      this.state = { step: 1, boxSize: null, burgers: {}, extras: {} };
      this.data.burgers.forEach((b) => (this.state.burgers[b.id] = 0));
      this.data.extras.forEach((e) => (this.state.extras[e.id] = 0));
      this.drawerBurger = null;
      this.observeStepbarHeight();
      this.bind();
      this.render();
      this.finishLoading();
    }

    reducedMotion() { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }

    async finishLoading() {
      const loader = this.q('[data-configurator-loader]');
      if (!loader) return;
      const images = [...this.q('[data-step-panel="1"]').querySelectorAll('img')];
      const ready = Promise.all(images.map((image) => image.decode?.().catch(() => {}) || Promise.resolve()));
      let readinessTimeout;
      await Promise.all([
        new Promise((resolve) => window.setTimeout(resolve, this.reducedMotion() ? 0 : 1400)),
        Promise.race([ready, new Promise((resolve) => { readinessTimeout = window.setTimeout(resolve, 3500); })])
      ]);
      window.clearTimeout(readinessTimeout);
      if (loader.hidden) return;
      try {
        if (!this.reducedMotion() && loader.animate) {
          await loader.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 400, easing: 'ease-out' }).finished;
        }
      } catch (_) { /* A removed theme-editor section can cancel the animation. */ }
      finally {
        loader.hidden = true;
        window.clearTimeout(Number(this.root.dataset.loaderTimeout));
      }
    }

    animateStep() {
      const panel = this.q(`[data-step-panel="${this.state.step}"]`);
      this.panelAnimation?.cancel();
      if (!this.reducedMotion() && panel.animate) {
        this.panelAnimation = panel.animate(
          [{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'translateY(0)' }],
          { duration: 280, easing: 'cubic-bezier(.22,1,.36,1)' }
        );
      }
    }

    q(sel) { return this.root.querySelector(sel); }
    qa(sel) { return [...this.root.querySelectorAll(sel)]; }
    selectedBox() { return this.data.boxes.find((b) => b.size === this.state.boxSize); }
    resolvedExtra(extra) {
      const boxVariant = extra.variantsByBox?.[String(this.state.boxSize)];
      return boxVariant ? { ...extra, ...boxVariant } : extra;
    }
    totalBurgers() { return Object.values(this.state.burgers).reduce((a, b) => a + b, 0); }
    extrasTotal() { return this.data.extras.reduce((s, e) => s + this.state.extras[e.id] * this.resolvedExtra(e).price, 0); }
    orderTotal() { return (this.selectedBox()?.price || 0) + this.extrasTotal(); }

    observeStepbarHeight() {
      const stepbar = this.q('.sbx-configurator__stepbar');
      const updateHeight = () => this.root.style.setProperty('--sbx-stepbar-height', `${Math.ceil(stepbar.getBoundingClientRect().height)}px`);
      updateHeight();
      this.stepbarResizeObserver = new ResizeObserver(updateHeight);
      this.stepbarResizeObserver.observe(stepbar);
    }

    bind() {
      this.qa('[data-box-card]').forEach((card) => card.addEventListener('click', () => this.chooseBox(Number(card.dataset.size))));
      this.qa('[data-burger-plus]').forEach((button) => button.addEventListener('click', () => this.changeBurger(button.closest('[data-burger-card]').dataset.burgerId, 1)));
      this.qa('[data-burger-minus]').forEach((button) => button.addEventListener('click', () => this.changeBurger(button.closest('[data-burger-card]').dataset.burgerId, -1)));
      this.qa('[data-burger-details]').forEach((button) => button.addEventListener('click', () => this.openDrawer(button.closest('[data-burger-card]').dataset.burgerId)));
      this.qa('[data-extra-plus]').forEach((button) => button.addEventListener('click', () => this.changeExtra(button.closest('[data-extra-card]').dataset.extraId, 1)));
      this.qa('[data-extra-minus]').forEach((button) => button.addEventListener('click', () => this.changeExtra(button.closest('[data-extra-card]').dataset.extraId, -1)));
      this.q('[data-next]').addEventListener('click', () => this.next());
      this.q('[data-back]').addEventListener('click', () => this.back());
      this.q('[data-progress-toggle]').addEventListener('click', () => this.toggleProgress());
      this.qa('[data-drawer-close]').forEach((b) => b.addEventListener('click', () => this.closeDrawer()));
      const dialog = this.q('[data-drawer]');
      dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.closeDrawer(); });
      dialog.addEventListener('click', (event) => { if (event.target === dialog) this.closeDrawer(); });
      this.q('[data-drawer-plus]').addEventListener('click', () => this.drawerBurger && this.changeBurger(this.drawerBurger, 1));
      this.q('[data-drawer-minus]').addEventListener('click', () => this.drawerBurger && this.changeBurger(this.drawerBurger, -1));
    }

    chooseBox(size) {
      const changed = this.state.boxSize !== null && this.state.boxSize !== size;
      if (changed) {
        Object.keys(this.state.burgers).forEach((k) => (this.state.burgers[k] = 0));
        Object.keys(this.state.extras).forEach((k) => (this.state.extras[k] = 0));
      }
      this.state.boxSize = size;
      this.render();
    }

    changeBurger(id, delta) {
      if (!this.state.boxSize) return;
      if (delta > 0 && this.totalBurgers() >= this.state.boxSize) return;
      this.state.burgers[id] = Math.max(0, this.state.burgers[id] + delta);
      this.render();
    }

    changeExtra(id, delta) {
      this.state.extras[id] = Math.max(0, this.state.extras[id] + delta);
      this.render();
    }

    canContinue() {
      if (this.state.step === 1) return Boolean(this.state.boxSize);
      if (this.state.step === 2) return this.totalBurgers() === this.state.boxSize;
      if (this.state.step === 3) return true;
      return false;
    }

    next() { if (this.canContinue() && this.state.step < 4) { this.state.step += 1; this.render(); window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); } }
    back() { if (this.state.step > 1) { this.state.step -= 1; this.render(); window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); } }
    editSelection() { this.state.step = 2; this.render(); window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); }
    restart() { this.state.boxSize = null; Object.keys(this.state.burgers).forEach((k) => (this.state.burgers[k] = 0)); Object.keys(this.state.extras).forEach((k) => (this.state.extras[k] = 0)); this.state.step = 1; this.render(); window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); }

    toggleProgress() {
      const p = this.q('[data-progress-toggle]');
      p.classList.toggle('is-open');
      p.setAttribute('aria-expanded', p.classList.contains('is-open') ? 'true' : 'false');
    }

    openDrawer(id) {
      this.qa('[data-allergens] details, details[data-allergens]').forEach((details) => { details.open = false; });
      this.drawerBurger = id;
      const b = this.data.burgers.find((x) => x.id === id);
      const allergenComponents = {
        classic: ['bun', 'beef', 'cheddar', 'pickles'],
        fresh: ['bun', 'beef', 'cheddar', 'pickles', 'fresh'],
        spicy: ['bun', 'beef', 'cheddar', 'pickles', 'spicy'],
        bbq: ['bun', 'beef', 'cheddar', 'pickles', 'bbq', 'onions', 'bacon'],
        redoat: ['bun', 'oat', 'cheddar', 'pickles', 'fresh'],
        'redoat-vegan': ['bun', 'oat', 'cheddar-vegan', 'pickles', 'fresh-vegan'],
        'kimchi-veggie': ['bun', 'kimchi', 'cheddar', 'pickles', 'fresh'],
        kimchi: ['bun', 'kimchi', 'cheddar-vegan', 'pickles', 'fresh-vegan']
      };
      const components = allergenComponents[id] || [];
      this.qa('[data-allergen-component]').forEach((entry) => {
        entry.hidden = !components.includes(entry.dataset.allergenComponent);
      });
      const missingAllergens = this.q('[data-allergens-missing]');
      missingAllergens.hidden = components.length > 0 && id !== 'classic';
      missingAllergens.textContent = components.length === 0
        ? 'Die Zutaten- und Allergenangaben für diesen Burger werden noch ergänzt.'
        : 'Die Zutaten- und Allergenangaben zu Senf und Ketchup werden noch ergänzt.';
      this.q('[data-drawer-tag]').textContent = b.tag;
      this.q('[data-drawer-title]').textContent = b.name;
      this.q('[data-drawer-image]').src = b.image;
      this.q('[data-drawer-image]').alt = b.name;
      this.q('[data-drawer-description]').textContent = b.description;
      this.q('[data-drawer-base]').textContent = b.base;
      this.q('[data-drawer-ingredients]').innerHTML = b.ingredients.map((i) => `<div class="sbx-ingredient">${i}</div>`).join('');
      const noteWrap = this.q('[data-drawer-note-wrap]');
      noteWrap.hidden = !b.note;
      this.q('[data-drawer-note]').textContent = b.note || '';
      this.q('[data-drawer-qty]').textContent = this.state.burgers[id];
      const dialog = this.q('[data-drawer]');
      this.drawerOpener = document.activeElement;
      this.previousOverflow = document.documentElement.style.overflow;
      dialog.hidden = false;
      dialog.showModal();
      this.q('.sbx-drawer__scroll').scrollTop = 0;
      this.q('.sbx-drawer__close').focus({ preventScroll: true });
      document.documentElement.style.overflow = 'hidden';
    }
    closeDrawer() {
      const dialog = this.q('[data-drawer]');
      if (!dialog.open) return;
      dialog.close();
      dialog.hidden = true;
      document.documentElement.style.overflow = this.previousOverflow || '';
      this.drawerOpener?.focus({ preventScroll: true });
    }

    async addToCart() {
      const box = this.selectedBox();
      const items = [];
      const selectedBurgerText = this.data.burgers.filter((b) => this.state.burgers[b.id] > 0).map((b) => `${this.state.burgers[b.id]}× ${b.name}`).join(', ');
      if (!box?.variantId) { this.showError('Bitte im Theme-Editor zuerst die 4er-, 6er- und 8er-Smashboxx Produkte zuordnen.'); return; }
      items.push({ id: box.variantId, quantity: 1, properties: { 'Burger-Auswahl': selectedBurgerText } });
      for (const extra of this.data.extras) {
        const qty = this.state.extras[extra.id];
        if (!qty) continue;
        const resolvedExtra = this.resolvedExtra(extra);
        if (!resolvedExtra.variantId) { this.showError(`Bitte im Theme-Editor das Produkt „${extra.name}“ zuordnen.`); return; }
        items.push({ id: resolvedExtra.variantId, quantity: qty });
      }
      try {
        const res = await fetch(this.data.cartAddUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        if (!res.ok) throw new Error((await res.json()).description || 'Warenkorb konnte nicht aktualisiert werden.');
        window.location.assign('/checkout');
      } catch (e) { this.showError(e.message || 'Beim Hinzufügen zum Warenkorb ist ein Fehler aufgetreten.'); }
    }

    showError(message) {
      const summary = this.q('[data-summary]');
      let error = summary.querySelector('.sbx-error');
      if (!error) { error = document.createElement('div'); error.className = 'sbx-error'; summary.appendChild(error); }
      error.textContent = message;
    }

    render() {
      const stepChanged = this.renderedStep !== undefined && this.renderedStep !== this.state.step;
      this.qa('[data-step-panel]').forEach((p) => p.classList.toggle('is-active', Number(p.dataset.stepPanel) === this.state.step));
      this.qa('[data-step-indicator]').forEach((s) => { const n = Number(s.dataset.stepIndicator); s.classList.toggle('is-active', n === this.state.step); s.classList.toggle('is-complete', n < this.state.step); });
      
this.qa('[data-box-card]').forEach((card) => {
  const size = Number(card.dataset.size);

  const boxLabels = {
    4: 'S',
    6: 'M',
    8: 'XL'
  };

  const selected = size === this.state.boxSize;

  card.classList.toggle('is-selected', selected);

  card.querySelector('[data-box-select]').textContent =
    selected
      ? '✓ Boxx ' + boxLabels[size] + ' ausgewählt'
      : 'Boxx ' + boxLabels[size] + ' zusammenstellen';
});

      this.qa('[data-burger-card]').forEach((card) => { const q = this.state.burgers[card.dataset.burgerId]; card.classList.toggle('is-selected', q > 0); card.querySelector('[data-burger-qty]').textContent = q; });
      this.qa('[data-extra-card]').forEach((card) => {
        const extra = this.data.extras.find((item) => item.id === card.dataset.extraId);
        const q = this.state.extras[card.dataset.extraId];
        card.classList.toggle('is-selected', q > 0);
        card.querySelector('[data-extra-qty]').textContent = q;
        card.querySelector('[data-extra-price]').textContent = formatMoney(this.resolvedExtra(extra).price);
      });
      const friesPortions = this.q('[data-fries-portions]');
      if (friesPortions) friesPortions.textContent = this.state.boxSize ? `${this.state.boxSize} Portionen – passend zur ${this.state.boxSize}er Smashboxx` : 'Portionsmenge passend zu deiner gewählten Box';
      if (this.drawerBurger && !this.q('[data-drawer]').hidden) this.q('[data-drawer-qty]').textContent = this.state.burgers[this.drawerBurger];
      this.renderProgress();
      this.renderFooter();
      this.renderFreeShipping();
      if (this.state.step === 4) {
        this.renderSummary();
        this.renderShippingEstimate();
      }
      if (stepChanged) this.animateStep();
      this.renderedStep = this.state.step;
    }

    renderFreeShipping() {
      const threshold = 10000;
      const total = this.orderTotal();
      const remaining = Math.max(0, threshold - total);
      const container = this.q('[data-free-shipping]');
      container.hidden = !this.selectedBox();
      container.classList.toggle('is-complete', remaining === 0);
      const message = remaining > 0
        ? `Noch ${formatMoney(remaining)} bis zum kostenlosen Versand.`
        : 'Kostenloser Versand erreicht!';
      const label = this.q('[data-free-shipping-text]');
      if (label.textContent !== message) label.textContent = message;
      this.q('[data-free-shipping-fill]').style.width = `${Math.min(100, total / threshold * 100)}%`;
      const progress = this.q('[data-free-shipping-progress]');
      progress.setAttribute('aria-valuenow', String(Math.min(threshold, total) / 100));
      progress.setAttribute('aria-valuetext', message);
    }

    renderProgress() {
      const n = this.totalBurgers(); const max = this.state.boxSize || 0; const complete = max > 0 && n === max;
      this.q('[data-burger-count]').textContent = `${n} von ${max} Burgern gewählt${complete ? ' ✓' : ''}`;
      this.q('[data-progress-fill]').style.width = max ? `${Math.min(100, (n / max) * 100)}%` : '0%';
      this.q('[data-progress-toggle]').classList.toggle('is-complete', complete);
      const chosen = this.data.burgers.filter((b) => this.state.burgers[b.id] > 0);
      this.q('[data-progress-list]').innerHTML = chosen.length ? chosen.map((b) => `<div class="sbx-progress-item"><span>${b.name}</span><strong>${this.state.burgers[b.id]}×</strong></div>`).join('') : '<div class="sbx-muted">Noch keine Burger ausgewählt.</div>';
    }

    renderFooter() {
      const back = this.q('[data-back]'); const next = this.q('[data-next]');
      back.style.visibility = this.state.step === 1 ? 'hidden' : 'visible';
      next.style.display = this.state.step === 4 ? 'none' : '';
      next.textContent = this.state.step === 1 && !this.state.boxSize ? 'Smashboxx auswählen' : 'Weiter';
      next.disabled = !this.canContinue();
      const burgerComplete = this.state.step === 2 && this.state.boxSize && this.totalBurgers() === this.state.boxSize;
      const boxReady = this.state.step === 1 && Boolean(this.state.boxSize);
      const soft = (this.state.step === 1 && !this.state.boxSize) || (this.state.step === 2 && this.state.boxSize && !burgerComplete);
      next.classList.toggle('is-soft', Boolean(soft));
      next.classList.toggle('is-ready', Boolean(boxReady || burgerComplete));
    }

    renderSummary() {
      const box = this.selectedBox(); if (!box) return;
      const burgers = this.data.burgers.filter((b) => this.state.burgers[b.id] > 0).map((b) => `<div class="sbx-summary-product"><img src="${b.image}" alt=""><div><strong>${this.state.burgers[b.id]} × ${b.name}</strong><div class="sbx-muted">${b.tag}</div></div></div>`).join('');
      const burgersWithNotes = this.data.burgers.filter((b) => this.state.burgers[b.id] > 0 && b.note);
      const shoppingNoteHtml = burgersWithNotes.length ? `<div class="sbx-shopping-note"><h4>Bitte frisch ergänzen</h4><p>Empfindliche Frischezutaten wie Salat und Tomaten würden durch Transport und Tiefkühlung an Qualität verlieren. Zwiebeln und Jalapeños kannst du außerdem frisch und genau nach deinem Geschmack dosieren.</p><ul>${burgersWithNotes.map((b) => `<li><strong>${b.name}:</strong> ${b.note.replace(' nicht enthalten.', '')}</li>`).join('')}</ul></div>` : '';
      const extras = this.data.extras.filter((e) => this.state.extras[e.id] > 0).map((e) => this.resolvedExtra(e));
      const extrasHtml = extras.length ? extras.map((e) => `<div class="sbx-summary-product"><div class="sbx-summary-thumb sbx-summary-thumb--extra">${e.image ? `<img src="${e.image.replace(/&/g, '&amp;').replace(/"/g, '&quot;')}" alt="" width="64" height="64" loading="lazy">` : 'Extra'}</div><div><strong>${this.state.extras[e.id]} × ${e.name}</strong><div class="sbx-muted">${formatMoney(e.price)} pro Stück</div></div><div class="sbx-summary-extra-actions"><strong>${formatMoney(this.state.extras[e.id] * e.price)}</strong><button type="button" class="sbx-extra-remove" data-extra-remove="${e.id}" aria-label="${e.name.replace(/&/g, '&amp;').replace(/"/g, '&quot;')} vollständig entfernen" title="Extra entfernen"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18M9 6V4h6v2M5 6l1 14h12l1-14M10 10v6M14 10v6"/></svg></button></div></div>`).join('') : '<p class="sbx-muted">Keine Extras gewählt.</p>';
      this.q('[data-summary]').innerHTML = `<div class="sbx-summary-box"><h3>${box.title}</h3><div class="sbx-summary-row"><span>Festpreis</span><strong>${formatMoney(box.price)}</strong></div><p class="sbx-muted">${box.size} Burger · die Burgerwahl verändert den Boxpreis nicht</p>${burgers}${shoppingNoteHtml}</div><div class="sbx-summary-box"><h3>Extras</h3>${extrasHtml}<div class="sbx-summary-row"><span>Extras gesamt</span><strong>${formatMoney(this.extrasTotal())}</strong></div><div class="sbx-summary-row"><strong>GESAMT</strong><strong>${formatMoney(this.orderTotal())}</strong></div><div class="sbx-tax-note">inkl. MwSt., zzgl. Versandkosten</div><button type="button" class="sbx-select-button" data-cart-add>Jetzt zur Kasse – ${formatMoney(this.orderTotal())}</button><div class="sbx-summary-actions"><button type="button" class="sbx-button sbx-button--secondary" data-edit>Burgerauswahl ändern</button><button type="button" class="sbx-button sbx-button--secondary" data-restart>Neu starten</button></div><section class="sbx-shipping-estimate" data-shipping-estimate aria-labelledby="SbxShippingEstimate"><h3 id="SbxShippingEstimate">Versand &amp; Lieferung</h3><p><strong>Voraussichtlicher Versand:</strong> <span data-shipping-date></span><br><strong>Voraussichtliche Lieferung:</strong> <span data-delivery-date></span></p><p class="sbx-shipping-estimate__note">Wir versenden Montag bis Donnerstag. Bestellungen bis 12 Uhr gehen in der Regel noch am selben Tag raus. Ab Donnerstag 12 Uhr erfolgt der Versand am Montag. Die Lieferung per Express erfolgt in der Regel am Folgetag.</p></section></div>`;
      this.qa('[data-extra-remove]').forEach((button) => button.addEventListener('click', () => {
        this.state.extras[button.dataset.extraRemove] = 0;
        this.render();
        (this.q('[data-extra-remove]') || this.q('[data-cart-add]')).focus({ preventScroll: true });
      }));
      this.q('[data-cart-add]').addEventListener('click', () => this.addToCart());
      this.q('[data-edit]').addEventListener('click', () => this.editSelection());
      this.q('[data-restart]').addEventListener('click', () => this.restart());
    }

    shippingEstimate() {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      }).formatToParts(new Date()).reduce((values, part) => ({ ...values, [part.type]: part.value }), {});
      const today = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
      const minutes = Number(parts.hour) * 60 + Number(parts.minute);
      let shippingDay = today;
      while (true) {
        const weekday = new Date(shippingDay).getUTCDay();
        const isShippingDay = weekday >= 1 && weekday <= 4;
        const canShipToday = shippingDay !== today || minutes <= 720;
        if (isShippingDay && canShipToday) break;
        shippingDay += 86400000;
      }
      const deliveryDay = shippingDay + 86400000;
      const dateFormat = new Intl.DateTimeFormat('de-DE', {
        timeZone: 'Europe/Berlin', weekday: 'long', day: 'numeric', month: 'long'
      });
      return { shipping: dateFormat.format(new Date(shippingDay)), delivery: dateFormat.format(new Date(deliveryDay)) };
    }

    renderShippingEstimate() {
      const shipping = this.q('[data-shipping-estimate]');
      if (!shipping) return;
      const estimate = this.shippingEstimate();
      this.q('[data-shipping-date]').textContent = estimate.shipping;
      this.q('[data-delivery-date]').textContent = estimate.delivery;
    }
  }

  const init = () => document.querySelectorAll('.smashboxx-configurator').forEach((root) => { if (!root.dataset.sbxInitialized) { root.dataset.sbxInitialized = 'true'; new SmashboxxConfigurator(root); } });
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
  document.addEventListener('shopify:section:load', init);
})();
